import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Collapse,
  Tooltip,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SaveIcon from '@mui/icons-material/Save';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CalculateIcon from '@mui/icons-material/Calculate';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import { useVoyagerStore } from '../../stores/useVoyagerStore';
import { CalculatedAttribute } from '../../types';
import { TRINO_FUNCTIONS, TRINO_FUNCTION_CATEGORIES, TrinoFunctionDef } from '../../constants';
import { getDimensionsByGroup, getMetricsByGroup, toDisplayName } from '../../utils';
import CaseWhenValueEditor from './CaseWhenValueEditor';
import {
  CalculatedRoot,
  FieldPickerContainer,
  FieldPickerColumn,
  OperatorBar,
  FunctionGrid,
} from './styles';

const OPERATORS = ['+', '-', '*', '/', 'CASE WHEN'] as const;

interface CalculatedComponentsProps {
  onClose?: () => void;
  editingAttribute?: CalculatedAttribute | null;
}

const CalculatedComponents: React.FC<CalculatedComponentsProps> = ({ onClose, editingAttribute }) => {
  const [formulaName, setFormulaName] = useState('');
  const [formula, setFormula] = useState('');
  const [showCaseWhen, setShowCaseWhen] = useState(false);
  const [fieldType, setFieldType] = useState<'metric' | 'dimension'>('dimension');
  const [pendingCloseParen, setPendingCloseParen] = useState(false);
  const [activeFnCategory, setActiveFnCategory] = useState<string | null>(null);

  const isEditing = !!editingAttribute;

  // Pre-populate fields when editing
  useEffect(() => {
    if (editingAttribute) {
      setFormulaName(editingAttribute.name);
      setFormula(editingAttribute.formula);
      setFieldType(editingAttribute.type);
    } else {
      setFormulaName('');
      setFormula('');
      setFieldType('dimension');
    }
    setShowCaseWhen(false);
    setPendingCloseParen(false);
    setActiveFnCategory(null);
  }, [editingAttribute]);

  const selectedMetrics = useVoyagerStore((state) => state.selectedMetrics);
  const selectedDimensions = useVoyagerStore(
    (state) => state.selectedDimensions,
  );
  const semanticModel = useVoyagerStore((state) => state.semanticModel);

  // All available dimensions from the model (for CASE WHEN builder)
  const allDimensions = React.useMemo(() => {
    if (!semanticModel?.dimensions) return selectedDimensions;
    return semanticModel.dimensions.map((d: { name: string }) => d.name);
  }, [semanticModel, selectedDimensions]);

  // Grouped metrics and dimensions from the full model (for field picker)
  const metricGroups = useMemo(
    () => (semanticModel ? getMetricsByGroup(semanticModel) : []),
    [semanticModel],
  );
  const dimensionGroups = useMemo(
    () => (semanticModel ? getDimensionsByGroup(semanticModel) : []),
    [semanticModel],
  );

  const handleFieldClick = (field: string) => {
    if (pendingCloseParen) {
      setFormula((prev) => `${prev}${field})`);
      setPendingCloseParen(false);
    } else {
      setFormula((prev) => (prev ? `${prev} ${field}` : field));
    }
  };

  const handleOperatorClick = (op: string) => {
    if (op === 'CASE WHEN') {
      setShowCaseWhen(true);
      return;
    }
    if (pendingCloseParen) {
      setFormula((prev) => `${prev}) ${op} `);
      setPendingCloseParen(false);
    } else {
      setFormula((prev) => (prev ? `${prev} ${op} ` : `${op} `));
    }
  };

  const handleCaseWhenGenerate = (sql: string) => {
    setFormula((prev) => (prev ? `${prev} ${sql}` : sql));
    setShowCaseWhen(false);
  };

  const handleFunctionClick = (fn: TrinoFunctionDef) => {
    switch (fn.insertMode) {
      case 'no_arg':
        setFormula((prev) => (prev ? `${prev} ${fn.template}` : fn.template));
        break;
      case 'single_arg':
        setFormula((prev) => (prev ? `${prev} ${fn.template}` : fn.template));
        setPendingCloseParen(true);
        break;
      case 'template':
        setFormula((prev) => (prev ? `${prev} ${fn.template}` : fn.template));
        break;
    }
  };

  const categoryIcon = (key: string) => {
    switch (key) {
      case 'string': return <TextFieldsIcon fontSize="small" />;
      case 'date': return <CalendarMonthIcon fontSize="small" />;
      case 'math': return <CalculateIcon fontSize="small" />;
      case 'conditional': return <AltRouteIcon fontSize="small" />;
      default: return undefined;
    }
  };

  // Sanitize name: replace non-alphanumeric (except _) with underscore, ensure starts with letter/_
  const sanitizeName = (raw: string): string => {
    let clean = raw.trim().replace(/[^a-zA-Z0-9_]/g, '_').replace(/_+/g, '_');
    if (clean && /^[0-9]/.test(clean)) {
      clean = '_' + clean;
    }
    return clean.toLowerCase();
  };

  const handleSave = () => {
    if (!formulaName.trim() || !formula.trim()) return;

    const safeName = sanitizeName(formulaName);
    if (!safeName) return;

    const store = useVoyagerStore.getState();

    if (isEditing && editingAttribute) {
      store.updateCalculatedField(editingAttribute.id, {
        name: safeName,
        formula: formula.trim(),
        type: fieldType,
      });
    } else {
      store.addCalculatedField({
        name: safeName,
        formula: formula.trim(),
        type: fieldType,
      });
    }

    // Reset
    setFormulaName('');
    setFormula('');
    setShowCaseWhen(false);
    setFieldType('dimension');

    if (onClose) onClose();
  };

  const handleClear = () => {
    setFormula('');
    setShowCaseWhen(false);
    setPendingCloseParen(false);
    setActiveFnCategory(null);
  };

  const namePreview = formulaName.trim() ? sanitizeName(formulaName) : '';

  return (
    <CalculatedRoot>
      <Typography variant="h6">
        {isEditing ? 'Edit Calculated Field' : 'Calculated Field Builder'}
      </Typography>

      <TextField
        label="Formula Name"
        value={formulaName}
        onChange={(e) => setFormulaName(e.target.value)}
        fullWidth
        size="small"
        placeholder="e.g., revenue_per_user"
        helperText={namePreview && namePreview !== formulaName.trim() ? `SQL alias: ${namePreview}` : ''}
      />

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Use as</Typography>
        <ToggleButtonGroup
          value={fieldType}
          exclusive
          onChange={(_e, val) => { if (val) setFieldType(val); }}
          size="small"
        >
          <ToggleButton value="metric" sx={{ textTransform: 'none', px: 2 }}>
            Metric
          </ToggleButton>
          <ToggleButton value="dimension" sx={{ textTransform: 'none', px: 2 }}>
            Dimension
          </ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          {fieldType === 'metric' ? 'Aggregated value (not grouped)' : 'Grouping column (added to GROUP BY)'}
        </Typography>
      </Box>

      <Typography variant="subtitle2">Select Fields</Typography>
      <FieldPickerContainer>
        <FieldPickerColumn variant="outlined">
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ px: 1, py: 0.5, display: 'block', fontWeight: 600 }}
          >
            Metrics
          </Typography>
          <Divider />
          <List dense disablePadding sx={{ overflowY: 'auto', flex: 1 }}>
            {metricGroups.length === 0 ? (
              <Typography
                variant="body2"
                color="text.disabled"
                sx={{ p: 1, textAlign: 'center' }}
              >
                No metrics available
              </Typography>
            ) : (
              metricGroups.map((group) => (
                <React.Fragment key={group.label}>
                  <ListSubheader
                    disableSticky
                    sx={{ lineHeight: '28px', fontSize: 11, fontWeight: 700, bgcolor: 'var(--sunk)', color: 'text.secondary' }}
                  >
                    {group.label}
                  </ListSubheader>
                  {group.metrics.map((m) => (
                    <ListItemButton
                      key={m.name}
                      onClick={() => handleFieldClick(m.name)}
                      dense
                      sx={{ pl: 3 }}
                    >
                      <ListItemText
                        primary={toDisplayName(m.name)}
                        secondary={m.name}
                        primaryTypographyProps={{ variant: 'body2', fontSize: 12 }}
                        secondaryTypographyProps={{ fontFamily: 'monospace', fontSize: 10 }}
                      />
                    </ListItemButton>
                  ))}
                </React.Fragment>
              ))
            )}
          </List>
        </FieldPickerColumn>

        <FieldPickerColumn variant="outlined">
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ px: 1, py: 0.5, display: 'block', fontWeight: 600 }}
          >
            Dimensions
          </Typography>
          <Divider />
          <List dense disablePadding sx={{ overflowY: 'auto', flex: 1 }}>
            {dimensionGroups.length === 0 ? (
              <Typography
                variant="body2"
                color="text.disabled"
                sx={{ p: 1, textAlign: 'center' }}
              >
                No dimensions available
              </Typography>
            ) : (
              dimensionGroups.map((group) => (
                <React.Fragment key={group.label}>
                  <ListSubheader
                    disableSticky
                    sx={{ lineHeight: '28px', fontSize: 11, fontWeight: 700, bgcolor: 'var(--sunk)', color: 'text.secondary' }}
                  >
                    {group.label}
                  </ListSubheader>
                  {group.dimensions.map((d) => (
                    <ListItemButton
                      key={d.name}
                      onClick={() => handleFieldClick(d.name)}
                      dense
                      sx={{ pl: 3 }}
                    >
                      <ListItemText
                        primary={toDisplayName(d.name)}
                        secondary={d.name}
                        primaryTypographyProps={{ variant: 'body2', fontSize: 12 }}
                        secondaryTypographyProps={{ fontFamily: 'monospace', fontSize: 10 }}
                      />
                    </ListItemButton>
                  ))}
                </React.Fragment>
              ))
            )}
          </List>
        </FieldPickerColumn>
      </FieldPickerContainer>

      <Typography variant="subtitle2">Operators</Typography>
      <OperatorBar>
        {OPERATORS.map((op) => (
          <Button
            key={op}
            variant="outlined"
            size="small"
            onClick={() => handleOperatorClick(op)}
            sx={{
              minWidth: op === 'CASE WHEN' ? 'auto' : 36,
              fontFamily: 'monospace',
              fontWeight: 700,
            }}
          >
            {op}
          </Button>
        ))}
        <Button size="small" color="error" variant="text" onClick={handleClear}>
          Clear
        </Button>
      </OperatorBar>

      {/* Function Palette */}
      <Typography variant="subtitle2">Functions</Typography>
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {TRINO_FUNCTION_CATEGORIES.map((cat) => (
          <Chip
            key={cat.key}
            label={cat.label}
            size="small"
            icon={categoryIcon(cat.key)}
            variant={activeFnCategory === cat.key ? 'filled' : 'outlined'}
            color={activeFnCategory === cat.key ? 'primary' : 'default'}
            onClick={() => setActiveFnCategory((prev) => prev === cat.key ? null : cat.key)}
          />
        ))}
      </Box>
      <Collapse in={activeFnCategory !== null} timeout="auto" unmountOnExit>
        <FunctionGrid>
          {TRINO_FUNCTIONS
            .filter((fn) => fn.category === activeFnCategory)
            .map((fn) => (
              <Tooltip key={fn.name} title={`${fn.signature} — ${fn.description}`} placement="top" arrow>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleFunctionClick(fn)}
                  sx={{ textTransform: 'none', fontFamily: 'monospace', fontWeight: 600, fontSize: '0.75rem' }}
                >
                  {fn.name}
                </Button>
              </Tooltip>
            ))}
        </FunctionGrid>
      </Collapse>

      {showCaseWhen && (
        <CaseWhenValueEditor
          dimensions={allDimensions}
          onGenerate={handleCaseWhenGenerate}
        />
      )}

      {pendingCloseParen && (
        <Typography variant="caption" color="primary" sx={{ fontStyle: 'italic' }}>
          Click a field to complete the function, or type directly in the formula
        </Typography>
      )}

      <Typography variant="subtitle2">Formula</Typography>
      <TextField
        multiline
        minRows={2}
        maxRows={6}
        fullWidth
        value={formula}
        onChange={(e) => { setFormula(e.target.value); setPendingCloseParen(false); }}
        placeholder="Click fields, operators and functions, or type directly..."
        InputProps={{
          sx: { fontFamily: 'monospace', fontSize: '0.875rem', backgroundColor: 'var(--sunk)' },
        }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={isEditing ? <SaveIcon /> : <AddCircleOutlineIcon />}
          onClick={handleSave}
          disabled={!formulaName.trim() || !formula.trim()}
        >
          {isEditing ? 'Save Changes' : 'Add to Selection'}
        </Button>
      </Box>
    </CalculatedRoot>
  );
};

export default CalculatedComponents;
